import type PlyrJS from 'plyr';
import * as React from 'react';
import type { DependencyList, MutableRefObject, Ref } from 'react';

export type PlyrInstance = InstanceType<typeof PlyrJS>;
export type PlyrOptions = ConstructorParameters<typeof PlyrJS>[1];
export type VideoSource = NonNullable<PlyrInstance['source']>;

export interface APITypes {
  plyr: PlyrInstance | null;
}

type CommonPlyrParams = {
  options?: PlyrOptions | null;
  onReady?: (player: PlyrInstance) => void;
};

type PlyrParams =
  | ({ kind: 'withSource'; source: VideoSource } & CommonPlyrParams)
  | ({ kind: 'noSource'; source?: null | undefined } & CommonPlyrParams);

export function useEmbeddedVideo(
  apiRef: Ref<APITypes>,
  params: PlyrParams,
  deps: DependencyList | null = null,
) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const plyrRef = React.useRef<PlyrInstance | null>(null);
  const latestSourceRef = React.useRef<VideoSource | null>(null);
  const [plyrInstance, setPlyrInstance] = React.useState<PlyrInstance | null>(
    null,
  );

  React.useImperativeHandle(
    apiRef,
    () => ({
      plyr: plyrInstance,
    }),
    [plyrInstance],
  );

  // Recreate only when options changes (or user supplies deps)
  const recreateDeps = deps ?? [params.options];
  React.useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    let isMounted = true;

    const setupPlayer = async () => {
      const { default: PlyrConstructor } = await import('plyr');
      if (!isMounted || !videoRef.current) return;

      if (plyrRef.current) {
        plyrRef.current.destroy();
        plyrRef.current = null;
      }

      const plyr = new PlyrConstructor(videoRef.current, params.options ?? {});
      plyrRef.current = plyr;
      setPlyrInstance(plyr);

      // Apply the latest source value (from ref) to handle race conditions
      // where source may have changed during async initialization
      if (params.kind === 'withSource' && latestSourceRef.current) {
        plyr.source = latestSourceRef.current;
      }

      params.onReady?.(plyr);
    };

    void setupPlayer();

    return () => {
      isMounted = false;
      plyrRef.current?.destroy();
      plyrRef.current = null;
      setPlyrInstance(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, recreateDeps);

  // Track latest source in ref to handle rapid changes during initialization
  React.useEffect(() => {
    if (params.kind === 'withSource') {
      latestSourceRef.current = params.source;
    } else {
      latestSourceRef.current = null;
    }
  }, [params.kind, params.source]);

  // Update source without recreating instance
  // Only update if player is already initialized to avoid race conditions
  React.useEffect(() => {
    const plyr = plyrRef.current;
    if (!plyr) return;

    if (params.kind === 'withSource') {
      plyr.source = params.source;
    }
  }, [params.kind, params.source]);

  return videoRef as MutableRefObject<HTMLVideoElement>;
}
